import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import {
  getInvestigation,
  getInvestigationReasoning,
  getInvestigationReasoningV1,
  getInvestigationReasoningFreshness,
  listInvestigationInquiries,
  listInvestigationReasoningHistory,
  listInvestigations,
} from './investigations.api';

export function useInvestigations(machineId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.investigations(auth.activeCustomerId, machineId),
    queryFn: () => listInvestigations(machineId),
    enabled: Boolean(enabled && auth.activeCustomerId && machineId),
  });
}

export function useInvestigation(investigationId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId),
    queryFn: () => getInvestigation(investigationId),
    enabled: Boolean(enabled && auth.activeCustomerId && investigationId),
  });
}


export function useInvestigationReasoning(investigationId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.investigationReasoning(auth.activeCustomerId, investigationId),
    queryFn: () => getInvestigationReasoning(investigationId),
    enabled: Boolean(enabled && auth.activeCustomerId && investigationId),
  });
}

export function useInvestigationReasoningFreshness(investigationId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.investigationReasoningFreshness(auth.activeCustomerId, investigationId),
    queryFn: () => getInvestigationReasoningFreshness(investigationId),
    enabled: Boolean(enabled && auth.activeCustomerId && investigationId),
    refetchInterval: (query) => {
      const status = query.state.data?.freshness?.reasoning_status;
      return status === 'REFRESH_PENDING' || status === 'PROCESSING' ? 3000 : false;
    },
  });
}

export function useInvestigationInquiries(investigationId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.investigationInquiries(auth.activeCustomerId, investigationId),
    queryFn: () => listInvestigationInquiries(investigationId),
    enabled: Boolean(enabled && auth.activeCustomerId && investigationId),
  });
}

export function useInvestigationReasoningHistory(investigationId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.investigationReasoningHistory(auth.activeCustomerId, investigationId),
    queryFn: () => listInvestigationReasoningHistory(investigationId),
    enabled: Boolean(enabled && auth.activeCustomerId && investigationId),
  });
}

/** Fails closed when writer selection cannot be fetched; no Legacy fallback. */
export function useInvestigationReasoningV1(investigationId: string, enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.investigationReasoningV1(auth.activeCustomerId, investigationId),
    queryFn: () => getInvestigationReasoningV1(investigationId),
    enabled: Boolean(enabled && auth.activeCustomerId && investigationId),
    refetchInterval: (query) => query.state.data?.writer === 'V1' ? 15000 : false,
  });
}
